import { useEffect } from 'react';

import { useQueryClient } from '@mint-lib/query-client';
import { Chat } from '@mint-sdk/api-ai-assistant';

import { mergeChatHistory } from '../helpers/mergeChatHistory.js';

/**
 * Pulls the committed history once when the user enters a chat that is already
 * cached, and merges it into the cache.
 *
 * The history query keeps `staleTime: Infinity`, and the socket only carries
 * what the server emits after it (re)connects: a reply that finished while the
 * user was in another chat never reaches it. A first visit is skipped because
 * `useQuery` fetches it anyway. The result is merged rather than replaced, so
 * a live socket message that lands while the request is in flight, or a prompt
 * the socket has not accepted yet, survives the sync.
 */
export const useSyncChatHistoryOnEnter = (chatId: string) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const urlParams = { chatSessionId: chatId };
    const { queryKey } = Chat.getHistoryById({ urlParams });

    if (queryClient.getQueryData(queryKey) === undefined) {
      return;
    }

    let isCurrent = true;

    Chat.getHistoryById
      .endpoint({ urlParams })
      .then((fresh) => {
        if (!isCurrent) {
          return;
        }

        queryClient.setQueryData(
          queryKey,
          (cached: Chat.ChatMessageRecordType[] | undefined) =>
            mergeChatHistory(fresh, cached),
        );
      })
      .catch(() => {});

    return () => {
      isCurrent = false;
    };
  }, [chatId, queryClient]);
};
